import { WhenAllReady } from "@nimble/react-requests/components/shared/when-all-ready";
import { getQueryData } from "@nimble/react-requests/data/response-utils";
import { useModalState } from "@nimble/react-state-hooks/use-modal-state";
import { InvitationItem } from "@nimble/recommendation-client/types";
import { useCallback } from "react";

import { RecommendationCard } from "@/app/(discover)/candidate/invites/(tabs)/invitations/components/chat-details/recommendation-card";
import { ResponseButtons } from "@/app/(discover)/candidate/invites/(tabs)/invitations/components/chat-details/response-buttons";
import AcceptMatchModal from "@/app/(discover)/candidate/invites/components/accept-match-modal/accept-match-modal";
import { ChatDetailView } from "@/app/(discover)/candidate/invites/components/chat/chat-detail-view";
import RejectMatchModal from "@/app/(discover)/candidate/invites/components/reject-match-modal/reject-match-modal";
import useBatchedSchoolData from "@/hooks/data/use-batched-school-data";

export default function InvitationChatDetailContainer({
  invitation,
  onClose,
  onRespond,
}: {
  invitation: InvitationItem;
  onClose: () => void;
  onRespond: () => void;
}) {
  const schoolQuery = useBatchedSchoolData({
    ncesId: invitation.school_nces_id,
  });
  const schoolData = getQueryData(schoolQuery);

  const {
    isOpen: isOpenRejectModal,
    onOpen: onOpenRejectModal,
    onClose: onCloseRejectModal,
  } = useModalState();
  const {
    isOpen: isOpenAcceptModal,
    onOpen: onOpenAcceptModal,
    onClose: onCloseAcceptModal,
  } = useModalState();

  const onResponseSubmitSuccess = useCallback(() => {
    onCloseRejectModal();
    onCloseAcceptModal();
    onRespond();
  }, [onCloseRejectModal, onCloseAcceptModal, onRespond]);

  return (
    <WhenAllReady queries={[schoolQuery]}>
      {schoolData && (
        <>
          <ChatDetailView onClose={onClose} schoolData={schoolData}>
            <RecommendationCard
              invitation={invitation}
              schoolData={schoolData}
              bottomContent={
                <ResponseButtons.Root
                  principalFirstName={schoolData.principal?.first_name}
                >
                  <ResponseButtons.Actions className="mt-1">
                    <ResponseButtons.SkipButton
                      onClick={onOpenRejectModal}
                      className="gap-1.5 px-2 py-2 font-medium text-ink-subtle shadow-none hover:border-line-strong hover:text-ink-emphasis has-[>svg]:px-2 sm:px-4 sm:has-[>svg]:px-4"
                    >
                      Skip for now
                    </ResponseButtons.SkipButton>
                    <ResponseButtons.InterestButton
                      onClick={onOpenAcceptModal}
                      className="flex-1 gap-1.5 px-2 py-2 shadow-none has-[>svg]:px-2 sm:px-4 sm:has-[>svg]:px-4"
                    >
                      I&apos;m interested
                    </ResponseButtons.InterestButton>
                  </ResponseButtons.Actions>
                  <ResponseButtons.Notes>
                    <ResponseButtons.InvitationInterestNote />
                    <ResponseButtons.PrivacyNote />
                  </ResponseButtons.Notes>
                </ResponseButtons.Root>
              }
            />
          </ChatDetailView>
          {isOpenRejectModal && (
            <RejectMatchModal
              onClose={onCloseRejectModal}
              onSubmitSuccess={onResponseSubmitSuccess}
              schoolNcesId={invitation.school_nces_id}
            />
          )}
          {isOpenAcceptModal && (
            <AcceptMatchModal
              onClose={onCloseAcceptModal}
              onSubmitSuccess={onResponseSubmitSuccess}
              schoolData={schoolData}
              source="invitation"
            />
          )}
        </>
      )}
    </WhenAllReady>
  );
}
